import React, { Component, type ComponentClass, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { NavLink, type RouteComponentProps } from "react-router-dom";
import { Button } from "reactstrap";
import { bindActionCreators } from "redux";
import { type InjectedFormProps, SubmissionError, reduxForm } from "redux-form";
import { DEFAULT_VIEW_PATH } from "src/app/ui/DefaultViewPath";
import NonAuthenticated from "src/app/ui/non-authenticated/NonAuthenticated";
import { ResetPassword } from "src/authentication/actions/PasswordActionCreator";
import ResetPasswordForm, {
  type IResetPasswordFormData,
  PASSWORD,
  PASSWORD_LENGTH,
} from "src/authentication/forms/ResetPasswordForm";
import type IResetPasswordURLParams from "src/authentication/objects/IResetPasswordURLParams";
import Loader from "src/shared/components/Loader";

export const RESET_PASSWORD_VIEW = "/pr/:resetToken";

interface IResetPasswordViewInnerProps
  extends IResetPasswordViewWithFormProps,
    InjectedFormProps<IResetPasswordFormData, IResetPasswordViewProps> {}

interface IResetPasswordViewInnerState {
  resetPasswordPromise?: Promise<void>;
}

class ResetPasswordViewInner extends Component<
  IResetPasswordViewInnerProps,
  IResetPasswordViewInnerState
> {
  public state = {
    resetPasswordPromise: undefined,
  };

  public onSubmit = async (formData: IResetPasswordFormData): Promise<void> => {
    const {
      ResetPassword,
      match: {
        params: { resetToken },
      },
    } = this.props;
    const resetPasswordPromise: Promise<void> = (async () => {
      try {
        return await ResetPassword(resetToken, formData[PASSWORD]);
      } catch (ex) {
        throw new SubmissionError({
          [PASSWORD]: (
            <FormattedMessage id="authentication.forget.reset.error" />
          ),
        });
      }
    })();

    this.setState({
      resetPasswordPromise,
    });

    await resetPasswordPromise;

    const { history } = this.props;
    history.push(DEFAULT_VIEW_PATH);
  };

  public render(): ReactNode {
    const { handleSubmit } = this.props;
    const { resetPasswordPromise } = this.state;
    return (
      <NonAuthenticated>
        <form onSubmit={handleSubmit(this.onSubmit)}>
          <h2>
            <FormattedMessage id="authentication.forget.reset.heading" />
          </h2>
          <p>
            <FormattedMessage
              id="authentication.forget.reset.description"
              values={{ length: PASSWORD_LENGTH }}
            />
          </p>

          <ResetPasswordForm />

          {resetPasswordPromise && <Loader promise={resetPasswordPromise} />}

          <p>
            <Button color="primary">
              <FormattedMessage id="authentication.forget.reset.submit" />
            </Button>
            <NavLink to={DEFAULT_VIEW_PATH}>
              <FormattedMessage id="authentication.forget.reset.return_to_login" />
            </NavLink>
          </p>
        </form>
      </NonAuthenticated>
    );
  }
}

interface IResetPasswordViewWithFormProps extends IResetPasswordViewProps {}

const ResetPasswordViewWithForm: ComponentClass<IResetPasswordViewWithFormProps> =
  reduxForm<IResetPasswordFormData, IResetPasswordViewProps>({
    form: "ResetPasswordForm",
  })(ResetPasswordViewInner);

export interface IResetPasswordViewProps
  extends RouteComponentProps<IResetPasswordURLParams> {
  ResetPassword: (resetToken: string, password: string) => Promise<void>;
}

export default connect(
  (state, props: IResetPasswordViewProps) => {
    return {};
  },
  (dispatch) =>
    bindActionCreators(
      {
        ResetPassword,
      },
      dispatch,
    ),
)(ResetPasswordViewWithForm);
